<!--
    This component encapsulates the requirements for Pulte customer passwords.
    It also visually illustrates each requirement, updating as the user types.

    Requirements are neither .met nor .unmet, until a value has been provided.

    To use this component, you MUST provide the password data via a property.
    If you would like to be notified when all requirements are met, you MAY
        also provide a callback function for @all-requirements-met.

    Example markup:

        <input type="password" v-model="pw" />

        <password-requirements
            :value="pw"
            @all-requirements-met="setPasswordIsValid">
        </password-requirements>

        <input type="submit" :disabled="!passwordIsValid" />

    Example parent component:

        {
            data() {
                return {
                    pw: "",
                    passwordIsValid: false
                };
            },
            methods: {
                setPasswordIsValid(value) {
                    this.passwordIsValid = value;
                }
            }
        }
-->

<template>
    <ul class="password-requirements chevron">
        <li v-if="!differentFromPrevious" class="unmet">
            Your new password must be different than the previous one.
        </li>
        <li v-if="!differentFromUsername" class="unmet">
            Your new password cannot be the same as your username.
        </li>
        <li :class="{ met: applyStyles && hasValidLength, unmet: applyStyles && !hasValidLength}">
            8-32 characters in length
        </li>
        <li :class="{ met: applyStyles && containsNumber, unmet: applyStyles && !containsNumber}">
            Include at least 1 number
        </li>
        <li :class="{ met: applyStyles && containsLetter, unmet: applyStyles && !containsLetter}">
            Include at least 1 letter
        </li>
    </ul>
</template>

<script>
    import { mapActions, mapGetters } from 'vuex';

    export default {
        name: 'password-requirements',
        props: {
            value: {
                // The current value of the password we are validating
                type: String,
                required: true
            },
            previousValue: {
                // The user's previous password, if applicable
                // This should be pulled from user input, NOT from the back end
                type: String,
                required: false
            }
        },
        data() {
            return {
                passwordHasChanged: false,
            }
        },
        computed: {
            ...mapGetters(['isAuthenticated', 'userProfile']),
            usernameFromToken() {
                if (this.isAuthenticated && this.userProfile) {
                    return this.userProfile["email_address"];
                }
                return null;
            },
            applyStyles() {
                return this.value || this.passwordHasChanged;
            },
            hasValidLength() {
                return this.value.length >= 8
                    && this.value.length <= 32;
            },
            containsNumber() {
                return /\d/.test(this.value);
            },
            containsLetter() {
                return /[a-zA-Z]/.test(this.value);
            },
            differentFromPrevious() {
                if (!this.previousValue) {
                    // Only applies if the user is CHANGING their password
                    return true;
                }
                return this.value !== this.previousValue;
            },
            differentFromUsername() {
                if (!this.usernameFromToken) {
                    // Only applies if we are able to find the current username
                    return true;
                }
                return this.value !== this.usernameFromToken;
            },
            meetsAllRequirements() {
                return this.hasValidLength
                    && this.containsNumber
                    && this.containsLetter
                    && this.differentFromPrevious
                    && this.differentFromUsername;
            }
        },
        watch: {
            value: {
                handler() {
                    this.passwordHasChanged = true;
                }
            },
            meetsAllRequirements: {
                handler(val) {
                    this.$emit('all-requirements-met', val);
                }
            }
        }
    };
</script>

<style scoped lang="scss">
    .password-requirements {
        .met {
            color: #088354;
        }

        .unmet {
            color: #d34428;
        }
    }
</style>